// Persistence adapter: the assistant's message store.
// See .claude/context/persistence.md for why this seam exists.
import { useMemo } from 'react';
import { useIndexedDBStore } from './idb/useIndexedDBStore';

export const MESSAGE_STORE_NAME = 'messages';

/**
 * Store-bound message-store methods, with `getAll` scoped to ONE session.
 *
 * The store holds rows from whatever sessions have written to it, and the only thing that
 * removes a past session's rows is `cleanOrphans` at message-history init — per document,
 * on mount. Identity is deliberately NOT a cross-tab record (adopting a peer's id mid-page
 * would split the analytics trail; see `platform/session/session.js#reconcileSession`), so a
 * tab that has been open across a session rotation keeps the outgoing id while a newer tab
 * resets to a new one. Both tabs' rows then sit in the same store, and an unfiltered read
 * renders the other session's conversation into whichever tab reloads first.
 *
 * So the read is scoped here rather than left to the mount-time prune: the filter matches
 * `cleanOrphans`' delete rule exactly (`row.sessionId === sessionId`), which makes "this store
 * shows only the current session" true at every read instead of true only until a peer writes.
 *
 * Passing no `sessionId` leaves the read unfiltered — identity can be momentarily unresolved,
 * and a blank history is worse than a stale one.
 *
 * @param {string} [sessionId] - the holder's session id; rows from any other session are hidden.
 * @returns {object} store-bound, promise-returning message-store methods
 *   ({ getAll, add, update, get, put, insertIfAbsent, clearMessages, cleanOrphans }).
 */
export const useMessageStore = (sessionId) => {
  const store = useIndexedDBStore(MESSAGE_STORE_NAME);

  // Memoized so `getAll` keeps a stable identity: it is handed out as
  // `iDBGetAll_messages` and sits in the orchestrator's useCallback deps.
  return useMemo(() => ({
    ...store,
    getAll: () => store.getAll().then((rows) => (
      sessionId ? rows.filter((row) => row.sessionId === sessionId) : rows
    )),
  }), [store, sessionId]);
};
